import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, IUserModel, ModelType, ResourceType, ResourceStatus, IVideoClipResource, VideoClipStatus } from '@packages/global';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import { XMarkIcon, ArrowUpIcon, CheckIcon, TrashIcon, PencilSquareIcon, VideoCameraIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import VideoSelector from '../../common/VideoSelector';
import AudioSelector from '../../common/AudioSelector';
import { DropdownSelect, CoinPriceDisplay } from '../../common/form';

interface LipSyncFormProps {
  videoClip: IVideoClip;
  models: IUserModel[];
  modelsLoading: boolean;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  resources?: IVideoClipResource[];
  isPolling?: boolean;
  onTriggerPoll?: () => Promise<void>;
  canvasMode?: boolean;
  externalAddAudio?: { url: string; nonce: number };
  onReferenceAudioCleared?: (url: string) => void;
  onCanvasSubmitSuccess?: () => void;
  initialParams?: Record<string, unknown>;
}

const statusColorConfig: Record<string, string> = {
  [ResourceStatus.PENDING]: 'text-gray-400',
  [ResourceStatus.GENERATING]: 'text-blue-400',
  [ResourceStatus.COMPLETED]: 'text-green-400',
  [ResourceStatus.FAILED]: 'text-red-400'
};

export default function LipSyncForm({
  videoClip,
  models,
  modelsLoading,
  refreshVideoClip,
  resources: externalResources = [],
  isPolling = false,
  onTriggerPoll,
  canvasMode = false,
  externalAddAudio,
  onReferenceAudioCleared,
  onCanvasSubmitSuccess,
  initialParams
}: LipSyncFormProps) {
  const [formData, setFormData] = useState({
    videoUrl: '',
    audioUrl: '',
    modelId: ''
  });
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [showAudioSelector, setShowAudioSelector] = useState(false);

  const filteredResources = externalResources
    .filter((r: any) => r.resourceType === ResourceType.LIP_SYNC_VIDEO)
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const isGenerating = externalResources.some((r: any) =>
    r.resourceType === ResourceType.LIP_SYNC_VIDEO &&
    (r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING)
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { alert, confirm } = useDialog();
  const { success, error } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  const lipSyncModels = models.filter(model => model.modelType === ModelType.VIDEO_LIPSYNC);
  const modelOptions = lipSyncModels.map(m => ({ value: m.id, label: m.title }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [filteredResources.length]);

  useEffect(() => {
    setFormData({
      videoUrl: videoClip.videoUrl || '',
      audioUrl: videoClip.scriptAudioUrl || '',
      modelId: lipSyncModels.length > 0 ? lipSyncModels[0].id : ''
    });
  }, [videoClip.id, videoClip.videoUrl, videoClip.scriptAudioUrl]);

  const lastExternalAudioNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddAudio) return;
    if (lastExternalAudioNonceRef.current === externalAddAudio.nonce) return;
    lastExternalAudioNonceRef.current = externalAddAudio.nonce;
    if (!externalAddAudio.url) return;

    setFormData(prev => ({
      ...prev,
      audioUrl: externalAddAudio.url,
    }));
  }, [externalAddAudio]);

  const clearReferenceAudio = () => {
    const removedUrl = formData.audioUrl;
    if (removedUrl) {
      onReferenceAudioCleared?.(removedUrl);
    }
    setFormData(prev => ({ ...prev, audioUrl: '' }));
  };

  useEffect(() => {
    if (videoClip.lastLipSyncParams) {
      try {
        const params = JSON.parse(videoClip.lastLipSyncParams);
        handleReuseParams(params);
      } catch (e) {
        console.warn('Failed to parse last lip sync params:', e);
      }
    }
  }, [videoClip.id]);

  const handleReuseParams = (params: any) => {
    setFormData(prev => ({
      ...prev,
      videoUrl: params.videoUrl || prev.videoUrl,
      audioUrl: params.audioUrl || prev.audioUrl,
      modelId: params.modelId || prev.modelId
    }));
  };

  const initialParamsAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialParams || initialParamsAppliedRef.current) return;
    initialParamsAppliedRef.current = true;
    handleReuseParams(initialParams);
  }, [initialParams]);

  const handleApplyVideo = async (videoUrl: string) => {
    try {
      await videoClipApi.updateVideoClip(videoClip.id, {
        videoUrl,
        status: VideoClipStatus.COMPLETED
      });

      success(t('common:videoApplied'));

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (err) {
      console.error('Apply video failed:', err);
      alert(t('common:videoApplyFailed'));
    }
  };

  const handleGenerate = async () => {
    if (!formData.modelId) {
      alert(t('lipSyncForm.selectModel'));
      return;
    }

    if (!formData.videoUrl) {
      alert(t('lipSyncForm.provideVideo'));
      return;
    }

    if (!formData.audioUrl) {
      alert(t('lipSyncForm.provideAudio'));
      return;
    }

    try {
      await videoClipResourceApi.generateLipSync(videoClip.id, formData);
      success(t('lipSyncForm.taskSubmitted'));
      onCanvasSubmitSuccess?.();

      if (onTriggerPoll) {
        setTimeout(() => onTriggerPoll(), 1000);
      }
    } catch (err) {
      console.error('提交对口型生成任务失败:', err);
      error(t('common:submitFailed'));
    }
  };

  const isDisabled = false;

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {!canvasMode && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-6">
            {filteredResources.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <VideoCameraIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('lipSyncForm.noRecords')}</p>
                <p className="text-xs mt-1">{t('lipSyncForm.startBelow')}</p>
              </div>
            ) : (
              filteredResources.map((resource) => {
              let params = null;
              try {
                params = typeof resource.generationParams === 'string'
                  ? JSON.parse(resource.generationParams)
                  : resource.generationParams;
              } catch (e) {
                console.warn('Failed to parse generationParams:', e);
              }
              const statusInfo = statusColorConfig[resource.status];
              const isCompleted = resource.status === ResourceStatus.COMPLETED && resource.resultUrl;

              return (
                <div key={resource.id} className="relative space-y-3 border-2 border-dashed border-gray-800 rounded-lg p-2 group">
                  {params && (
                    <button
                      onClick={() => handleReuseParams(params)}
                      className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 z-10"
                      title={t('common:useParams')}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}

                  {isCompleted && (
                    <video
                      src={resource.resultUrl}
                      controls
                      className="w-full rounded-lg"
                    />
                  )}

                  {(resource.status === ResourceStatus.GENERATING || resource.status === ResourceStatus.PENDING) && (
                    <div className="py-8">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">{t(`generationProgress.${resource.status === ResourceStatus.PENDING ? 'pending' : 'generating'}`)}...</span>
                      </div>
                    </div>
                  )}

                  {resource.status === ResourceStatus.FAILED && (
                    <div className="p-3 bg-red-900/30 rounded-lg text-red-400 text-sm">
                      {resource.errorMessage || t('common:generationFailed')}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    {isCompleted && (
                      <button
                        onClick={() => handleApplyVideo(resource.resultUrl!)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors"
                      >
                        <CheckIcon className="h-3 w-3" />
                        <span className='text-xs'>{t('lipSyncForm.applyVideo')}</span>
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        const confirmed = await confirm(t('common:deleteResourceConfirm'), t('common:deleteResource'), 'danger');
                        if (confirmed) {
                          try {
                            await videoClipResourceApi.deleteResource(resource.id);
                            success(t('common:resourceDeleted'));
                            if (onTriggerPoll) {
                              await onTriggerPoll();
                            }
                          } catch (err) {
                            console.error('删除资源失败:', err);
                            error(t('common:deleteFailed'));
                          }
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title={t('common:deleteResource')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30">
        <div className="px-4 py-3">
          <div className="space-y-3">
            {/* 视频预览和选择 */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">{t('lipSyncForm.videoLabel')}</label>
              <div className="flex gap-2">
                {formData.videoUrl ? (
                  <div className="flex-1 relative">
                    <video
                      src={formData.videoUrl}
                      className="w-full h-24 object-cover rounded-lg bg-black"
                      controls={false}
                    />
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, videoUrl: '' }))}
                      className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 h-24 bg-gray-800 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                    {t('lipSyncForm.noVideoSelected')}
                  </div>
                )}
                <button
                  onClick={() => setShowVideoSelector(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  {t('common:select')}
                </button>
              </div>
            </div>

            {/* 音频预览和选择 */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">{t('lipSyncForm.audioLabel')}</label>
              <div className="flex gap-2">
                {formData.audioUrl ? (
                  <div className="flex-1 relative">
                    <audio
                      src={formData.audioUrl}
                      controls
                      className="w-full h-10 dark:[filter:invert(1)_hue-rotate(180deg)]"
                    />
                    <button
                      type="button"
                      onClick={clearReferenceAudio}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                    {t('lipSyncForm.noAudioSelected')}
                  </div>
                )}
                <button
                  onClick={() => setShowAudioSelector(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                >
                  <MusicalNoteIcon className="h-4 w-4" />
                  {t('common:select')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DropdownSelect
                options={modelOptions}
                value={formData.modelId}
                onChange={(value) => setFormData(prev => ({ ...prev, modelId: value }))}
                placeholder={t('lipSyncForm.selectModel')}
                disabled={isDisabled}
                loading={modelsLoading}
                icon={<VideoCameraIcon className="h-4 w-4 text-purple-400" />}
              />
            </div>

            <div className="flex items-center gap-3">
              <CoinPriceDisplay modelId={formData.modelId} />
              <button
                onClick={handleGenerate}
                disabled={isDisabled || !formData.modelId || !formData.videoUrl || !formData.audioUrl}
                className={`p-2 rounded-full transition-colors ${isDisabled || !formData.modelId || !formData.videoUrl || !formData.audioUrl
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-600 hover:bg-gray-500 text-white'
                  }`}
              >
                {isDisabled ? (
                  <div className="h-5 w-5 border-2 border-gray-500 border-t-white rounded-full animate-spin" />
                ) : (
                  <ArrowUpIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <VideoSelector
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={(video) => {
          if (video.resultVideoUrl) {
            setFormData(prev => ({ ...prev, videoUrl: video.resultVideoUrl! }));
            setShowVideoSelector(false);
          }
        }}
        models={models}
      />

      <AudioSelector
        isOpen={showAudioSelector}
        onClose={() => setShowAudioSelector(false)}
        onSelect={(audio) => {
          if (audio.resultAudioUrl) {
            setFormData(prev => ({ ...prev, audioUrl: audio.resultAudioUrl! }));
            setShowAudioSelector(false);
          }
        }}
        models={models}
      />
    </div>
  );
}
